import type {
  INeoDashboardService,
  NeoDashboardError,
} from "../ports/driver/neoDashboardService";
import type {
  INeoDashboardRepository,
  NeoDashboardCsvCellEdit,
  NeoDashboardPatchPayload,
  NeoDashboardPayload,
} from "../ports/driven/neoDashboardRepository";

const toError = (error: unknown): NeoDashboardError => ({
  message: error instanceof Error ? error.message : "Something went wrong.",
});

export const NeoDashboardServiceImplFactory = ({
  neoDashboardRepository,
}: {
  neoDashboardRepository: INeoDashboardRepository;
}): INeoDashboardService => {
  const repo = neoDashboardRepository;

  const listDashboards: INeoDashboardService["listDashboards"] = async (
    projectId
  ) => {
    try {
      const resp = await repo.listDashboardsAPI(projectId);
      return { success: true, data: resp?.items ?? [] };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const createDashboard = async (
    projectId: number,
    payload: NeoDashboardPayload
  ) => {
    try {
      const data = await repo.createDashboardAPI(projectId, payload);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const getDashboard = async (projectId: number, dashboardId: number) => {
    try {
      const data = await repo.getDashboardAPI(projectId, dashboardId);
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const updateDashboard = async (
    projectId: number,
    dashboardId: number,
    payload: NeoDashboardPatchPayload
  ) => {
    try {
      const data = await repo.updateDashboardAPI(
        projectId,
        dashboardId,
        payload
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteDashboard = async (projectId: number, dashboardId: number) => {
    try {
      await repo.deleteDashboardAPI(projectId, dashboardId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const uploadDashboardCsv = async (
    projectId: number,
    dashboardId: number,
    file: File
  ) => {
    try {
      const data = await repo.uploadDashboardCsvAPI(
        projectId,
        dashboardId,
        file
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const fetchDashboardCsvContent = async (
    projectId: number,
    dashboardId: number,
    fileId: number
  ) => {
    try {
      const data = await repo.fetchDashboardCsvContentAPI(
        projectId,
        dashboardId,
        fileId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const downloadDashboardCsvXlsx = async (
    projectId: number,
    dashboardId: number,
    fileId: number
  ) => {
    try {
      const data = await repo.downloadDashboardCsvXlsxAPI(
        projectId,
        dashboardId,
        fileId
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const saveDashboardCsvEdits = async (
    projectId: number,
    dashboardId: number,
    fileId: number,
    edits: NeoDashboardCsvCellEdit[]
  ) => {
    try {
      const data = await repo.saveDashboardCsvEditsAPI(
        projectId,
        dashboardId,
        fileId,
        edits
      );
      return { success: true, data };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  const deleteDashboardCsv = async (
    projectId: number,
    dashboardId: number,
    fileId: number
  ) => {
    try {
      await repo.deleteDashboardCsvAPI(projectId, dashboardId, fileId);
      return { success: true, data: null };
    } catch (error) {
      return { success: false, error: toError(error) };
    }
  };

  return {
    listDashboards,
    createDashboard,
    getDashboard,
    updateDashboard,
    deleteDashboard,
    uploadDashboardCsv,
    fetchDashboardCsvContent,
    downloadDashboardCsvXlsx,
    saveDashboardCsvEdits,
    deleteDashboardCsv,
  };
};
